import { h } from '@logicflow/core';
import { DynamicGroupNode, DynamicGroupNodeModel } from '@logicflow/extension';
import { GroupType } from '../types/nodeTypes';

// 自定义动态分组节点视图 - 支持文字拖动
export class CustomDynamicGroupNode extends DynamicGroupNode {
  private textDragStartPos: { x: number; y: number } | null = null;
  private initialTextOffset: { x: number; y: number } | null = null;

  constructor(props: any) {
    super(props);
    this.handleTextMouseDown = this.handleTextMouseDown.bind(this);
    this.handleTextMouseMove = this.handleTextMouseMove.bind(this);
    this.handleTextMouseUp = this.handleTextMouseUp.bind(this);
  }

  // 重写getText方法来自定义文字渲染和交互
  getText() {
    const { model } = this.props;
    const { text } = model;

    if (!text || !text.value) {
      return null;
    }

    // 安全地获取文字位置，如果方法不存在则使用默认位置
    let textPos: { x: number; y: number };
    if (typeof (model as any).getTextPosition === 'function') {
      textPos = (model as any).getTextPosition();
    } else {
      // 备用方案：使用默认位置
      textPos = {
        x: model.x + ((model as any).textOffset?.x || 0),
        y: model.y + ((model as any).textOffset?.y || 0),
      };
    }

    // 判断分组类型（默认为状态分组）
    const groupType = model.properties?.groupType || GroupType.STATE;
    const isValueGroup = groupType === GroupType.VALUE;

    // 处理多行文本（使用原始文本，不包含后缀）
    const textLines = text.value.split('\n');
    const lineHeight = 16; // 行高
    const totalHeight = (textLines.length - 1) * lineHeight;

    // 如果只有一行文本，使用原来的简单渲染
    if (textLines.length === 1) {
      // 值分组需要特殊处理：使用 tspan 分别渲染文字和 [] 后缀以保持对齐
      if (isValueGroup) {
        return h(
          'text',
          {
            x: textPos.x,
            y: textPos.y,
            fill: '#000000',
            textAnchor: 'middle',
            dominantBaseline: 'middle',
            style: {
              cursor: (model as any).textDraggable ? 'move' : 'default',
              userSelect: 'none',
              fontSize: 14,
            },
            className: 'lf-node-text draggable-text',
            onMouseDown: this.handleTextMouseDown,
          },
          [
            h('tspan', { key: 'name' }, text.value),
            h('tspan', { key: 'suffix', dy: -1 }, ' []'),
          ]
        );
      }
      return h(
        'text',
        {
          x: textPos.x,
          y: textPos.y,
          fill: '#000000',
          textAnchor: 'middle',
          dominantBaseline: 'middle',
          style: {
            cursor: (model as any).textDraggable ? 'move' : 'default',
            userSelect: 'none',
            fontSize: 14,
          },
          className: 'lf-node-text draggable-text',
          onMouseDown: this.handleTextMouseDown,
        },
        text.value
      );
    }

    // 多行文本渲染
    return h(
      'g',
      {
        className: 'lf-node-text-group draggable-text',
        onMouseDown: this.handleTextMouseDown,
        style: {
          cursor: (model as any).textDraggable ? 'move' : 'default',
          userSelect: 'none',
        },
      },
      textLines.map((line, index) => {
        const isLastLine = index === textLines.length - 1;
        // 如果是值分组且是最后一行，添加 [] 后缀
        if (isValueGroup && isLastLine) {
          return h(
            'text',
            {
              x: textPos.x,
              y: textPos.y - totalHeight / 2 + index * lineHeight,
              fill: '#000000',
              textAnchor: 'middle',
              dominantBaseline: 'middle',
              style: {
                fontSize: 14,
                userSelect: 'none',
              },
              key: `line-${index}`,
            },
            [
              h('tspan', { key: 'text' }, line),
              h('tspan', { key: 'suffix', dy: 1 }, ' []'),
            ]
          );
        }
        return h(
          'text',
          {
            x: textPos.x,
            y: textPos.y - totalHeight / 2 + index * lineHeight,
            fill: '#000000',
            textAnchor: 'middle',
            dominantBaseline: 'middle',
            style: {
              fontSize: 14,
              userSelect: 'none',
            },
            key: `line-${index}`,
          },
          line
        );
      })
    );
  }

  // 处理文字鼠标按下事件
  handleTextMouseDown(e: MouseEvent) {
    const { model } = this.props;

    if (!(model as any).textDraggable) {
      return;
    }

    e.stopPropagation(); // 阻止事件冒泡，避免触发节点拖动
    e.preventDefault();

    // 记录开始拖动的位置和初始偏移量
    this.textDragStartPos = { x: e.clientX, y: e.clientY };
    this.initialTextOffset = { ...(model as any).textOffset };

    // 标记开始拖动文字
    (model as any).startTextDrag();

    // 添加全局鼠标事件监听
    document.addEventListener('mousemove', this.handleTextMouseMove);
    document.addEventListener('mouseup', this.handleTextMouseUp);

    // 移除日志以提升性能
  }

  // 处理文字鼠标移动事件
  handleTextMouseMove(e: MouseEvent) {
    if (!this.textDragStartPos || !this.initialTextOffset) {
      return;
    }

    const { model } = this.props;

    // 使用LogicFlow的坐标转换API来正确处理缩放
    // 这是最准确的方法，因为LogicFlow知道当前的变换状态
    const graphModel = model.graphModel;

    try {
      // 使用LogicFlow的getPointByClient方法来转换坐标
      const lf = graphModel?.lf;
      if (lf && typeof lf.getPointByClient === 'function') {
        // 获取当前鼠标位置的逻辑坐标
        const currentPoint = lf.getPointByClient(e.clientX, e.clientY);
        // 获取开始拖动时的逻辑坐标
        const startPoint = lf.getPointByClient(
          this.textDragStartPos.x,
          this.textDragStartPos.y
        );

        // 计算逻辑坐标的差值
        let logicalDeltaX = 0;
        let logicalDeltaY = 0;

        if (currentPoint && startPoint) {
          // 处理LogicFlow返回的不同格式
          const currentX =
            currentPoint.canvasOverlayPosition?.x ||
            currentPoint.domOverlayPosition?.x ||
            currentPoint.x ||
            currentPoint[0];
          const currentY =
            currentPoint.canvasOverlayPosition?.y ||
            currentPoint.domOverlayPosition?.y ||
            currentPoint.y ||
            currentPoint[1];
          const startX =
            startPoint.canvasOverlayPosition?.x ||
            startPoint.domOverlayPosition?.x ||
            startPoint.x ||
            startPoint[0];
          const startY =
            startPoint.canvasOverlayPosition?.y ||
            startPoint.domOverlayPosition?.y ||
            startPoint.y ||
            startPoint[1];

          if (
            typeof currentX === 'number' &&
            typeof currentY === 'number' &&
            typeof startX === 'number' &&
            typeof startY === 'number'
          ) {
            logicalDeltaX = currentX - startX;
            logicalDeltaY = currentY - startY;
          }
        }

        // 如果成功获取到逻辑坐标差值，就使用它
        if (logicalDeltaX !== 0 || logicalDeltaY !== 0) {
          const newOffsetX = this.initialTextOffset.x + logicalDeltaX;
          const newOffsetY = this.initialTextOffset.y + logicalDeltaY;

          // 限制文字不要偏移太远
          const maxOffset = 200;
          const constrainedOffsetX = Math.max(
            -maxOffset,
            Math.min(maxOffset, newOffsetX)
          );
          const constrainedOffsetY = Math.max(
            -maxOffset,
            Math.min(maxOffset, newOffsetY)
          );

          // 更新文字偏移量
          (model as any).setTextOffset(constrainedOffsetX, constrainedOffsetY);
          return;
        }
      }
    } catch (error) {
      console.warn('使用LogicFlow坐标转换失败:', error);
    }

    // 备用方案：简化的实现（可能在缩放时不够准确）
    const screenDeltaX = e.clientX - this.textDragStartPos.x;
    const screenDeltaY = e.clientY - this.textDragStartPos.y;

    // 直接使用屏幕移动距离，不进行缩放调整
    // 这在大多数情况下应该是准确的
    const newOffsetX = this.initialTextOffset.x + screenDeltaX;
    const newOffsetY = this.initialTextOffset.y + screenDeltaY;

    // 限制文字不要偏移太远
    const maxOffset = 200;
    const constrainedOffsetX = Math.max(
      -maxOffset,
      Math.min(maxOffset, newOffsetX)
    );
    const constrainedOffsetY = Math.max(
      -maxOffset,
      Math.min(maxOffset, newOffsetY)
    );

    // 更新文字偏移量
    (model as any).setTextOffset(constrainedOffsetX, constrainedOffsetY);
  }

  // 处理文字鼠标抬起事件
  handleTextMouseUp(e: MouseEvent) {
    const { model } = this.props;

    // 清理拖动状态
    this.textDragStartPos = null;
    this.initialTextOffset = null;
    (model as any).endTextDrag();

    // 移除全局鼠标事件监听
    document.removeEventListener('mousemove', this.handleTextMouseMove);
    document.removeEventListener('mouseup', this.handleTextMouseUp);

    // 移除日志以提升性能
  }

  // 重写getShape方法以添加文字拖动提示
  getShape() {
    const shape = super.getShape();
    const { model } = this.props;

    // 如果正在拖动文字，添加视觉提示
    if ((model as any).isDraggingText) {
      // 可以在这里添加拖动时的视觉提示，比如改变文字样式等
      return h(
        'g',
        {
          className: 'lf-dynamic-group-dragging-text',
        },
        [shape, this.getText()]
      );
    }

    return shape;
  }
}

// 扩展动态分组节点模型以支持锚点和文字拖动
export class CustomDynamicGroupNodeModel extends DynamicGroupNodeModel {
  // 文字拖动相关属性
  public textDraggable: boolean = true; // 标记文字是否可拖动
  public textOffset: { x: number; y: number } = { x: 0, y: 0 }; // 文字相对于节点中心的偏移量
  public isDraggingText: boolean = false; // 标记是否正在拖动文字

  constructor(data: any, graphModel: any) {
    super(data, graphModel);
    // 初始化时确保锚点显示
    this.isShowAnchor = true;

    // 初始化文字偏移量 - 优先从properties读取
    this.initTextOffset(data);

    // 如果没有文字内容，设置默认文字
    if (!data.text || !data.text.value) {
      this.text = {
        ...this.text,
        value: '分组节点',
        x: data.x || this.x,
        y: data.y || this.y,
      };
    }

    // 移除日志以提升性能
  }

  // 初始化节点数据时保存文字偏移量
  initNodeData(data: any) {
    super.initNodeData(data);
    // 再次确保文字偏移量正确初始化
    this.initTextOffset(data);
  }

  // 统一的文字偏移量初始化方法
  private initTextOffset(data: any) {
    if (data.properties?.textOffset) {
      this.textOffset = { ...data.properties.textOffset };
    } else if (data.textOffset) {
      // 备用：直接从data中读取
      this.textOffset = { ...data.textOffset };
    } else {
      // 默认值
      this.textOffset = { x: 0, y: 0 };
      // 移除日志以提升性能
    }
  }

  // 获取文字的绝对位置
  getTextPosition() {
    return {
      x: this.x + this.textOffset.x,
      y: this.y + this.textOffset.y,
    };
  }

  // 设置文字偏移量
  setTextOffset(offsetX: number, offsetY: number) {
    this.textOffset = { x: offsetX, y: offsetY };

    // 更新properties以便数据持久化
    const newProperties = {
      ...this.properties,
      textOffset: this.textOffset,
      textDraggable: this.textDraggable,
    };

    // 使用父类方法更新properties
    this.setProperties(newProperties);

    // 触发重新渲染
    this.setAttributes();

    // 触发图形数据变化事件，确保数据面板更新
    if (this.graphModel) {
      this.graphModel.eventCenter.emit('node:properties-change', {
        id: this.id,
        keys: ['textOffset'],
        preProperties: this.getProperties(),
        properties: this.getProperties(),
      });
    }
  }

  // 重写getProperties方法确保文字偏移量被保存
  getProperties() {
    const properties = super.getProperties();
    const result = {
      ...properties,
      textOffset: this.textOffset,
      textDraggable: this.textDraggable,
    };
    return result;
  }

  // 重写setProperties方法确保文字偏移量被正确更新
  setProperties(properties: any) {
    super.setProperties(properties);
    // 当properties被更新时，同步更新文字偏移量
    if (properties.textOffset) {
      this.textOffset = { ...properties.textOffset };
    }
  }

  // 检查坐标是否在文字区域内
  isPointInTextArea(x: number, y: number) {
    const textPos = this.getTextPosition();
    const textValue = this.text?.value || '';

    // 估算文字区域的大小（这是一个简化的实现）
    const fontSize = 12; // 默认字体大小
    const textWidth = textValue.length * fontSize * 0.6; // 估算文字宽度
    const textHeight = fontSize + 4; // 估算文字高度

    return (
      x >= textPos.x - textWidth / 2 &&
      x <= textPos.x + textWidth / 2 &&
      y >= textPos.y - textHeight / 2 &&
      y <= textPos.y + textHeight / 2
    );
  }

  // 开始拖动文字
  startTextDrag() {
    this.isDraggingText = true;
  }

  // 结束拖动文字
  endTextDrag() {
    this.isDraggingText = false;
  }

  // 重写getDefaultAnchor方法来提供锚点 - 这是正确的方法
  getDefaultAnchor() {
    // 返回标准矩形锚点，使分组节点可以主动连线
    const { x, y, width, height } = this;
    return [
      { x: x - width / 2, y: y, id: `${this.id}_left`, edgeAddable: true },
      { x: x + width / 2, y: y, id: `${this.id}_right`, edgeAddable: true },
      { x: x, y: y - height / 2, id: `${this.id}_top`, edgeAddable: true },
      { x: x, y: y + height / 2, id: `${this.id}_bottom`, edgeAddable: true },
    ];
  }

  // 使用简化的锚点样式
  getAnchorStyle() {
    return {
      fill: '#FFFFFF',
      stroke: '#1890ff',
      r: 4,
      strokeWidth: 2,
    };
  }

  // 重写setIsShowAnchor方法确保锚点常显
  setIsShowAnchor(isShowAnchor = true) {
    this.isShowAnchor = isShowAnchor;
  }

  // 重写isAllowConnected方法以控制连接行为
  isAllowConnected(sourceNode: any, targetNode: any) {
    // 允许分组节点主动连接其他节点（除了自己的子节点）
    return true;
  }
}
